import type { DashboardResolveWidgetLine } from "@t3tools/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, type ReactNode } from "react";

import { widgetChoices, type DirectEdit } from "./directEdits";
import { WidgetEditMenu } from "./WidgetEditMenu";

import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";

import { DashboardsPdfExportButton } from "../DashboardsPdfExportButton";
import { KpiGrid } from "../dashboardKit";
import { ArtifactParameterControls } from "./ArtifactParameterControls";
import { ArtifactStatusBanner, artifactBannerState } from "./ArtifactStatusBanner";
import { ArtifactWidget } from "./ArtifactWidget";
import { ArtifactExcelExportButton } from "./ArtifactExcelExportButton";
import { artifactRowSpans, artifactSpanClassName } from "./artifactLayout";
import {
  normalizeArtifactParameterValues,
  reconcileArtifactOptionValues,
  type ArtifactParameterValues,
} from "./artifactParameters";
import {
  dashboardArtifactQueryOptions,
  type ArtifactOptionsFetcher,
  type ArtifactResolveTransport,
} from "./artifactsReactQuery";
import type {
  ArtifactSpec,
  DashboardArtifactSummary,
  DashboardArtifactVersionSummary,
} from "./artifactTypes";
import {
  useArtifactNow,
  useArtifactParameterOptions,
  useArtifactResolve,
} from "./useArtifactResolve";

export type DashboardArtifactRendererProps = {
  /** Page controls shown beside the title, such as publish and version history. */
  actions?: ReactNode;
  artifact: DashboardArtifactSummary;
  className?: string;
  /** Parameter options from a dataset; defaults to the artifacts API. */
  fetchOptions?: ArtifactOptionsFetcher;
  /** Widgets the assistant just changed, briefly highlighted. */
  highlightedWidgetIds?: ReadonlySet<string> | undefined;
  /** Direct edits for the widgets; absent when the viewer can't edit this version. */
  widgetEditor?:
    | { busy: boolean; onEdit: (widgetId: string, edit: DirectEdit) => void }
    | undefined;
  /** Shown in place of the default text when the dashboard has nothing on it yet. */
  emptyNotice?: ReactNode | undefined;
  /** Hides the "Shared with the team" banner; draft and archived banners still show. */
  hidePublishedBanner?: boolean | undefined;
  /** Pins "now" for date presets and "Updated X min ago"; otherwise it ticks. */
  now?: Date;
  onParameterValuesChange: (values: ArtifactParameterValues) => void;
  /** Usually decoded from the URL; missing or invalid values fall back to defaults. */
  parameterValues: Readonly<Record<string, unknown>>;
  /** Assistant previews by widget id: shown in place of the widget's own data until it is saved. */
  previews?: ReadonlyMap<string, DashboardResolveWidgetLine> | undefined;
  spec: ArtifactSpec;
  /** Opens the resolve stream; defaults to the artifacts API, stories replay fixtures. */
  transport?: ArtifactResolveTransport;
  version: DashboardArtifactVersionSummary;
};

/**
 * Renders a decoded artifact spec with the dashboard kit. Parameters are
 * controlled so routes can keep them in the URL. The layout renders at once
 * with a skeleton per widget; one resolve stream fills each widget as its line
 * arrives, and a widget's error stays in that widget.
 */
export function DashboardArtifactRenderer(props: DashboardArtifactRendererProps) {
  const { artifact, spec, version } = props;
  const now = useArtifactNow(props.now);
  const requested = useMemo(
    () => normalizeArtifactParameterValues(spec.parameters, props.parameterValues),
    [spec.parameters, props.parameterValues],
  );
  const options = useArtifactParameterOptions({
    artifactId: artifact.id,
    fetchOptions: props.fetchOptions,
    now,
    spec,
    values: requested,
    versionId: version.id,
  });
  // A rep or territory change can leave the account outside its new options.
  const reconciled = reconcileArtifactOptionValues(
    spec.parameters,
    spec.datasets,
    requested,
    options,
  );
  const { values } = reconciled;
  const reconciledKey = reconciled.changed ? JSON.stringify(values) : null;
  const { onParameterValuesChange } = props;
  useEffect(() => {
    if (reconciledKey !== null) onParameterValuesChange(JSON.parse(reconciledKey));
  }, [reconciledKey, onParameterValuesChange]);
  const resolve = useArtifactResolve({
    artifactId: artifact.id,
    now,
    parameters: spec.parameters,
    paused: reconciled.pending,
    transport: props.transport,
    values,
    versionId: version.id,
  });

  return (
    <div
      className={cn("space-y-4", props.className)}
      data-dashboard-artifact={artifact.slug}
      aria-busy={resolve.isStreaming}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">{spec.title}</h1>
          {spec.description ? (
            <p className="mt-1 text-sm text-muted-foreground">{spec.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {props.actions}
          <ArtifactExcelExportButton
            busy={!resolve.isComplete || (props.previews?.size ?? 0) > 0}
            now={now}
            spec={spec}
            values={values}
            version={version}
            widgetState={resolve.widgetState}
          />
          <DashboardsPdfExportButton
            title={spec.title}
            variant={props.actions ? "outline" : "default"}
          />
        </div>
      </div>
      {props.hidePublishedBanner &&
      artifactBannerState(artifact, version) === "published" ? null : (
        <ArtifactStatusBanner artifact={artifact} version={version} />
      )}
      <ArtifactParameterControls
        now={now}
        parameters={spec.parameters}
        values={values}
        optionsData={options.data}
        onChange={props.onParameterValuesChange}
      />
      {spec.rows.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-border/80 px-6 py-12 text-center"
          data-artifact-empty=""
        >
          {props.emptyNotice ?? (
            <>
              <p className="text-sm font-medium text-foreground">This dashboard is empty</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Describe what you want to see and the assistant will add it, for example "inbound
                calls by hour".
              </p>
            </>
          )}
        </div>
      ) : null}
      {spec.rows.map((row) => {
        const spans = artifactRowSpans(row);
        const widgets = row.widgets.map((widget, index) => {
          const preview = widget.kind === "text" ? undefined : props.previews?.get(widget.id);
          const highlighted = props.highlightedWidgetIds?.has(widget.id) === true;
          const state =
            widget.kind === "text"
              ? undefined
              : preview
                ? ({ line: preview, refreshing: false, status: "ready" } as const)
                : resolve.widgetState(widget.id);
          return (
            <div
              key={widget.id}
              className={cn(
                "group/widget relative min-w-0 rounded-xl transition-shadow duration-500",
                artifactSpanClassName(spans[index]!),
                highlighted && "ring-2 ring-primary/60 ring-offset-2 ring-offset-background",
              )}
              data-artifact-widget={widget.id}
              data-artifact-highlight={highlighted ? "" : undefined}
              data-artifact-preview={preview ? "" : undefined}
            >
              {props.widgetEditor ? (
                <WidgetEditMenu
                  choices={widgetChoices(spec, widget.id)}
                  disabled={props.widgetEditor.busy}
                  onEdit={(edit) => props.widgetEditor?.onEdit(widget.id, edit)}
                  title={widget.kind === "text" ? "text block" : widget.title}
                />
              ) : null}
              <ArtifactWidget
                artifactTitle={spec.title}
                now={now}
                spec={spec}
                widget={widget}
                state={state}
              />
            </div>
          );
        });
        return (
          <section key={row.widgets[0]!.id} className="space-y-2" aria-label={row.title}>
            {row.title ? (
              <h2 className="text-sm font-semibold text-muted-foreground">{row.title}</h2>
            ) : null}
            {row.widgets.every((widget) => widget.kind === "kpi") ? (
              <KpiGrid className="md:grid-cols-12">{widgets}</KpiGrid>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-12">{widgets}</div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function DashboardArtifactSkeleton(props: { className?: string | undefined }) {
  return (
    <div className={cn("space-y-4", props.className)} aria-label="Loading dashboard">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

/** Loads an artifact by id or slug, then renders it. */
export function DashboardArtifactLoader(
  props: Omit<DashboardArtifactRendererProps, "artifact" | "spec" | "version"> & {
    artifactRef: string;
    version?: number;
  },
) {
  const { artifactRef, version, ...rendererProps } = props;
  const query = useQuery(dashboardArtifactQueryOptions(artifactRef, version));
  if (query.isPending) return <DashboardArtifactSkeleton className={props.className} />;
  if (query.error) {
    return (
      <div
        role="alert"
        className={cn(
          "rounded-lg border border-destructive/32 bg-destructive/4 p-3 text-sm text-foreground",
          props.className,
        )}
      >
        {query.error.message}
      </div>
    );
  }
  return (
    <DashboardArtifactRenderer
      {...rendererProps}
      artifact={query.data.artifact}
      spec={query.data.spec}
      version={query.data.version}
    />
  );
}
